import { useLocation, Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Phone, GraduationCap, CheckCircle2, Award, Stethoscope,
  MessageCircle, ChevronRight, Info,
} from "lucide-react";
import { ROLE_HUBS } from "../data/roleHubs";
import AuthorCard from "../components/site/AuthorCard";
import MedicalDisclaimer from "../components/site/MedicalDisclaimer";
import { usePageMeta, usePageJsonLd } from "../hooks/usePageMeta";
import {
  AUTHOR_REF, AUTHOR_REF_MANISHA, PUBLISHER_REF, SITE, breadcrumb, graph,
} from "../lib/schema";
import { CLINIC, waLink, telLink } from "../lib/clinic";

// Single dynamic page component that renders any entry from /data/roleHubs.js
// Routes are registered in App.js like `/best-cataract-surgeon-in-varanasi`
// (Phase A role hubs) and `/insurance/ayushman-bharat-eye-hospital-varanasi`
// (Phase F intent hubs).  All content is unique per slug — no templated
// filler — so each page ships a genuine 800+ word body with real scenarios
// and role-specific credentials.

const Section = ({ title, children, testid }) => (
  <section className="mt-12 pt-10 border-t border-border" data-testid={testid}>
    <h2 className="font-serif text-2xl sm:text-3xl leading-tight text-balance">{title}</h2>
    <div className="mt-6">{children}</div>
  </section>
);

const RoleHub = () => {
  // Every role/intent hub uses a static route with a hard-coded pathname,
  // so we don't need :slug params — just strip the leading slash from the
  // pathname and look it up in the dictionary.
  const { pathname } = useLocation();
  const key = pathname.replace(/^\//, "").replace(/\/$/, "");
  const data = ROLE_HUBS[key];
  const canonicalPath = data ? `/${data.slug}` : "";

  usePageMeta({
    title: data?.title || "",
    path: canonicalPath,
    description: data?.description || "",
  });

  usePageJsonLd(
    data
      ? graph(
          [
            breadcrumb([
              { name: "Home", path: "/" },
              { name: data.h1, path: canonicalPath },
            ]),
            {
              "@type": "MedicalWebPage",
              "@id": `${SITE}${canonicalPath}#page`,
              url: `${SITE}${canonicalPath}`,
              name: data.h1,
              description: data.description,
              inLanguage: "en-IN",
              author: data.primaryDoctor === "manisha" ? AUTHOR_REF_MANISHA : AUTHOR_REF,
              reviewedBy: data.primaryDoctor === "manisha" ? AUTHOR_REF_MANISHA : AUTHOR_REF,
              publisher: PUBLISHER_REF,
              specialty: { "@type": "MedicalSpecialty", name: "Ophthalmology" },
              about: data.medicalConditionSchema
                ? { "@type": "MedicalCondition", name: data.medicalConditionSchema }
                : undefined,
              // Speakable snippets — helps voice-assistant citations
              // (Alexa, Google Assistant) and modern AI search preview cards.
              speakable: {
                "@type": "SpeakableSpecification",
                cssSelector: [".role-hub-hero-lead", ".role-hub-faq-answer"],
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE}${canonicalPath}#faq`,
              mainEntity: (data.faqs || []).map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ].filter(Boolean)
        )
      : null
  );

  if (!data) return <Navigate to="/" replace />;

  const doctorName =
    data.primaryDoctor === "manisha" ? "Dr. Manisha Mishra" : "Dr. Shantanu Kumar Gupta";
  const doctorLink = data.primaryDoctor === "manisha" ? "/team/dr-manisha-mishra" : "/about";

  return (
    <div data-testid={`role-hub-${data.slug.replace(/[^a-z0-9]+/gi, "-")}`}>
      {/* ─────────── Hero ─────────── */}
      <section className="pt-20 pb-6 container-page">
        <div className="overline mb-4 inline-flex items-center gap-2">
          <Award size={12} /> {data.overline}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-balance max-w-4xl">
          {data.h1}
        </h1>
        <p className="mt-5 max-w-3xl prose-elegant role-hub-hero-lead">{data.heroLead}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link to="/appointment" className="btn-primary text-xs inline-flex items-center gap-2" data-testid="rh-cta-appointment">
            Book consultation <ArrowRight size={13} />
          </Link>
          <a
            href={waLink(`Hi, I'd like a consultation for ${data.h1}.`)}
            target="_blank"
            rel="noopener"
            className="btn-outline text-xs inline-flex items-center gap-2"
          >
            <MessageCircle size={12} /> WhatsApp us
          </a>
          <a href={telLink()} className="btn-outline text-xs inline-flex items-center gap-2">
            <Phone size={12} /> {CLINIC.phone}
          </a>
        </div>
        <div className="mt-6 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Reviewed by{" "}
          <Link to={doctorLink} className="text-[hsl(var(--primary))] normal-case tracking-normal text-sm font-medium hover:underline">
            {doctorName}
          </Link>
        </div>
      </section>

      {/* ─────────── Why us ─────────── */}
      <Section title="Why patients choose us for this care" testid="rh-why-us">
        <div className="grid md:grid-cols-2 gap-6">
          {data.whyMe.map((w) => (
            <div key={w.title} className="p-5 rounded-2xl border border-border bg-background">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[hsl(var(--primary))] shrink-0 mt-0.5" />
                <div>
                  <div className="font-medium">{w.title}</div>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{w.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─────────── Services covered ─────────── */}
      {data.coveredServices?.length > 0 && (
        <Section title="Services & pages related to this specialty" testid="rh-services">
          <div className="grid sm:grid-cols-2 gap-4">
            {data.coveredServices.map((s) => (
              <Link
                to={s.href}
                key={s.href}
                className="group p-5 rounded-2xl border border-border bg-background hover:border-[hsl(var(--primary))]/40 transition-colors"
                data-testid={`rh-service-${s.href.replace(/\W+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[15px]">{s.label}</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.blurb}</div>
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground group-hover:text-[hsl(var(--primary))] group-hover:translate-x-0.5 transition-all shrink-0 mt-1" />
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* ─────────── Credentials ─────────── */}
      <Section title={`Credentials — ${doctorName}`} testid="rh-credentials">
        <div className="grid sm:grid-cols-2 gap-4">
          {data.credentials.map((c) => (
            <div key={c.label} className="p-4 rounded-xl border border-border bg-background flex items-start gap-3">
              <GraduationCap size={16} className="text-[hsl(var(--accent))] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-medium">{c.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{c.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ─────────── Case scenarios ─────────── */}
      {data.scenarios?.length > 0 && (
        <Section title="Real (anonymised) case scenarios from our clinic" testid="rh-scenarios">
          <div className="grid md:grid-cols-3 gap-5">
            {data.scenarios.map((s) => (
              <div key={s.title} className="p-5 rounded-2xl border border-border bg-[hsl(var(--secondary))]/20">
                <Stethoscope size={16} className="text-[hsl(var(--accent))] mb-3" />
                <div className="font-serif text-lg leading-snug">{s.title}</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* ─────────── Who should see ─────────── */}
      {data.whoShouldSee?.length > 0 && (
        <Section title="You should consult if you have any of these" testid="rh-who-should-see">
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {data.whoShouldSee.map((w) => (
              <li key={w} className="flex items-start gap-2 text-sm">
                <CheckCircle2 size={14} className="text-[hsl(var(--primary))] shrink-0 mt-1" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* ─────────── Consultation flow ─────────── */}
      {data.consultationFlow?.length > 0 && (
        <Section title="What a consultation actually looks like" testid="rh-flow">
          <ol className="space-y-4">
            {data.consultationFlow.map((f, idx) => (
              <li key={f.step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/8 text-[hsl(var(--primary))] flex items-center justify-center text-sm font-medium shrink-0">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-[15px]">{f.step}</div>
                  <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* ─────────── FAQ ─────────── */}
      {data.faqs?.length > 0 && (
        <Section title="Frequently asked questions" testid="rh-faq">
          <div className="divide-y divide-border rounded-2xl border border-border overflow-hidden">
            {data.faqs.map((f) => (
              <details key={f.q} className="group open:bg-[hsl(var(--secondary))]/20" data-testid="rh-faq-item">
                <summary className="cursor-pointer list-none p-5 flex items-start gap-4 hover:bg-[hsl(var(--secondary))]/20">
                  <Info size={14} className="mt-1 text-[hsl(var(--accent))] shrink-0" />
                  <div className="flex-1 font-medium text-[15px] pr-6">{f.q}</div>
                  <span className="text-[hsl(var(--accent))] text-lg leading-none select-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 pl-14 text-sm text-muted-foreground leading-relaxed role-hub-faq-answer">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </Section>
      )}

      {/* ─────────── Author + CTA ─────────── */}
      <section className="pt-14 pb-24 container-page grid md:grid-cols-[1fr_320px] gap-10">
        <div>
          <h2 className="font-serif text-2xl">Ready to book?</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-xl">
            Same-week appointments are common. Bring your previous reports, medications and glasses to the visit.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/appointment" className="btn-primary text-xs inline-flex items-center gap-2">
              Book consultation <ArrowRight size={13} />
            </Link>
            <a href={telLink()} className="btn-outline text-xs inline-flex items-center gap-2">
              <Phone size={12} /> Call
            </a>
          </div>
          <MedicalDisclaimer />
        </div>
        <aside className="self-start">
          <AuthorCard doctorSlug={data.primaryDoctor} />
        </aside>
      </section>
    </div>
  );
};

export default RoleHub;
